import type { BillingInfo } from './types.js';
import { Button } from '@studio/design-system/button';
import { Spinner } from '@studio/design-system/spinner';
import { dayjs } from '@studio/js-utils/time';
import { clsx } from 'cn';
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { twMerge } from 'cn';

interface Props {
  billing: BillingInfo;
  className?: string;
  onUpgrade: () => void;
  canUpgrade?: boolean;
}

/** Upcoming Invoice component. */

const UsageEntry = ({
  className,
  leftLabel,
  leftBottomLabel,
  progressClassName,
  progressInPercent,
  rightBottomLabel,
  rightLabel,
  subtitle,
  title,
  type
}: {
  className?: string;
  leftLabel: ReactNode;
  leftBottomLabel?: ReactNode;
  progressClassName?: string;
  progressInPercent: number;
  rightBottomLabel?: ReactNode;
  rightLabel: ReactNode;
  subtitle: ReactNode;
  title: ReactNode;
  type: 'error' | 'none' | 'success' | 'warning';
}) => (
  <div className={twMerge(clsx('flex', className))}>
    <div
      className={clsx('flex items-center gap-x-8 border-r-1 border-r-base-100 p-20', 'w-[164px]')}
    >
      <i
        className={clsx('i-mdi-circle text-sm', {
          'text-error': type === 'error',
          'text-warning': type === 'warning',
          'text-success': type === 'success',
          'text-[#383838]': type === 'none'
        })}
      />
      <div className='text-sm'>
        <div>{title}</div>
        <div className='mt-8 text-secondary'>{subtitle}</div>
      </div>
    </div>
    <div className='grow p-20'>
      <div className='flex items-center justify-between'>
        <div className='text-sm text-muted'>{leftLabel}</div>
        <div className='text-sm text-muted'>{rightLabel}</div>
      </div>
      <div className='relative mt-6 h-4 w-full rounded-full bg-[#383838]'>
        <div
          className={twMerge(
            clsx(
              'absolute inset-0 right-auto',
              {
                'bg-error': type === 'error',
                'bg-warning': type === 'warning',
                'bg-success': type === 'success'
              },
              progressClassName
            )
          )}
          style={{
            width: `${Math.min(progressInPercent, 100)}%`
          }}
        />
      </div>
      {(!!rightBottomLabel || !!leftBottomLabel) && (
        <div className='mt-8 flex'>
          {leftBottomLabel}
          <div className='grow' />
          {rightBottomLabel}
        </div>
      )}
    </div>
  </div>
);

export const CloudUsage = ({ billing, className, onUpgrade, canUpgrade = true }: Props) => {
  const {
    activePlan: { planSystem },
    loaded
  } = billing;
  const { quotaIncluded, quotaUsed } = billing.usage;

  const usagePercent = (quotaUsed / quotaIncluded) * 100;

  const quotaRemaining = Math.max(quotaIncluded - quotaUsed, 0);

  const overageUI = useMemo(() => {
    if (planSystem === 'free') {
      return null;
    }

    // Per ENG-3727: hide the overage row when a paid-plan room has a zero overage
    // rate — this typically means a missing/broken Stripe link upstream (backend
    // returns planOverageRate=0). Displaying "$0.00 per GB" would be a false promise.
    // PM-flagged in PR body: alternative is to show "$0.00 per GB" instead of hiding.
    if (
      parseFloat(billing.activePlan.overagePrice.value) === 0 &&
      billing.activePlan.overagePrice.currency === '$'
    ) {
      return null;
    }

    const overage = Math.max(quotaUsed - quotaIncluded, 0);

    const overageUnitPrice = billing.activePlan.overagePrice.value;
    const overageUnitPriceNumber = parseFloat(overageUnitPrice);
    const overageCurrency = billing.activePlan.overagePrice.currency;

    const overageTotalPrice = overage * overageUnitPriceNumber;

    const noun = quotaIncluded > 0 ? 'Overage' : 'Usage';

    return (
      <UsageEntry
        className='border-t-1 border-t-base-100'
        leftLabel={overage === 0 ? `No ${noun}` : `${formatCloudValue(overage, 4)} GB`}
        progressInPercent={(overageTotalPrice / billing.upcomingBilling.amount) * 100}
        rightLabel={
          overageTotalPrice === 0 ? '' : formatCloudValue(overageTotalPrice, 2, overageCurrency)
        }
        subtitle={`${overageCurrency}${overageUnitPrice} per GB`}
        title={noun}
        type={overage > 0 ? 'warning' : 'none'}
      />
    );
  }, [billing]);

  const leftBottomLabel = useMemo(() => {
    // Existing purchase recovery and non-owner views remain informational.
    if (!canUpgrade) return usagePercent >= 100 ? 'You have reached included usage limit.' : null;
    if (
      (billing.activePlan.planSystem === 'free' || billing.activePlan.planSystem === 'standard') &&
      usagePercent >= 100
    ) {
      return (
        <div>
          You have reached included usage limit.{' '}
          <button type='button' onClick={onUpgrade} className='text-primary underline'>
            Upgrade Now
          </button>
        </div>
      );
    }
    if (billing.activePlan.planSystem === 'free') {
      return (
        <button type='button' onClick={onUpgrade} className='text-primary underline'>
          Upgrade now to get more usage
        </button>
      );
    }

    return null;
  }, [billing, onUpgrade, usagePercent, canUpgrade]);

  const rightBottomLabel = useMemo(() => {
    if (!canUpgrade) return null;
    const upgrade =
      billing.activePlan.planSystem === 'free' ||
      (billing.activePlan.planSystem === 'standard' && usagePercent >= 100);
    if (upgrade) {
      return <Button onClick={onUpgrade}>Upgrade Plan</Button>;
    }
    return null;
  }, [billing, onUpgrade, usagePercent, canUpgrade]);

  if (!loaded) {
    return (
      <div className={twMerge('rounded-8 bg-dark pb-10', className)}>
        <div className='border-b-1 border-b-base-100 p-20'>
          <div className='mb-0 text-lg font-demibold leading-100'>Cloud Usage</div>
        </div>

        <Spinner className='mx-auto my-32' />
      </div>
    );
  }

  return (
    <div className={twMerge('rounded-8 bg-dark', className)}>
      <div className='border-b-1 border-b-base-100 p-20'>
        <div className='mb-0 text-lg font-demibold leading-100'>
          Cloud Usage
          {(billing.upcomingBilling.from ?? 0) !== 0 && (
            <span className='pl-12 text-md text-primary'>
              {dayjs(billing.upcomingBilling.from).format('M/D/YYYY')}
              <span className='mx-4'>-</span>
              {dayjs(billing.upcomingBilling.to).format('M/D/YYYY')}
            </span>
          )}
        </div>
      </div>
      {quotaIncluded > 0 && (
        <UsageEntry
          className='border-t-1 border-t-base-100'
          leftLabel={`${trimNumber(Math.min(billing.usage.quotaUsed, quotaIncluded))} GB Used`}
          progressInPercent={trimNumber(usagePercent)}
          leftBottomLabel={leftBottomLabel}
          rightBottomLabel={rightBottomLabel}
          rightLabel={`${trimNumber(quotaRemaining)} GB`}
          subtitle={`${trimNumber(quotaIncluded)} GB`}
          title='Included'
          type={trimNumber(usagePercent) >= 100 ? 'error' : 'success'}
        />
      )}
      {overageUI}
    </div>
  );
};

const trimNumber = (num: number, precision = 4): number => {
  const factor = 10 ** precision;
  return Math.round(num * factor) / factor;
};

// Display precision must not turn a real positive quantity or charge into zero.
const formatCloudValue = (value: number, precision: number, currency = ''): string => {
  const minimum = 10 ** -precision;
  if (value > 0 && value < minimum) return `<${currency}${minimum.toFixed(precision)}`;
  return `${currency}${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: currency ? precision : 0,
    maximumFractionDigits: precision
  }).format(value)}`;
};
